import * as React from 'react';

/** Material UI Packages. */
import { Typography, Link, IconButton, Tooltip } from '@mui/material';
import { GridActionsCellItem, GridEditInputCell, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-pro';

/** Material UI Icons. */
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileUploadIcon from '@mui/icons-material/FileUpload';

/** Internal Components. */
import {
    containerSizeList,
    prealert_status,
    isf_status,
    pierpass_import_status,
    hbl_release,
    customs_status,
    customs_hold_status,
    proof_status,
    empty_status,
    delivery_order_status,
    arrival_notice_status,
    invoice_crdr_status,
    trucking_type,
} from 'shared/config/constantsData';
import {
    CustomAutocompleteCell,
    CustomAutocompleteCell_string,
    CustomSelectCell_string,
    CustomAutocompleteCell_TradePartner,
    FillDownHeader,
    MultiLinkCell,
    MultiLinkEditCell,
    DateTypingEditCell,
} from 'shared/ui/RenderCell';
import CustomDetailPanelToggle from 'shared/ui/CustomDetailPanelToggle';
import { customized_style } from 'shared/config/Styles';
import GoogleRenderCell from 'shared/ui/GoogleRenderCell';
import { createHeaderGroup, createMultiHeaderGroup } from 'shared/ui/Tooltip';
import 'shared/config/Styles.css';
import { getClassName, getHeaderClass } from 'shared/lib/todoColorHandler';
import { parseDateToLocal } from 'shared/lib/dateFormatter';
import {
    createOptionPasteParser,
    createOptionValueParser,
    createTradePartnerValueParser,
    tradePartnerValueFormatter,
    parsePastedDate,
    objectValueFormatter,
    createObjectValueParser,
} from 'shared/lib/datagridHelpers';
import { isAssistantManagerOrAbove } from 'shared/lib/roles';

export const hblCols = (
    userRole,
    presetData,
    tab,
    todo,
    unitOptions,
    handleUnitChange,
    tradePartnerOptions,
    handleTradePartnerChange,
    handleFormatMismatch,
) => {
    const preAlert = presetData?.pre_alert;
    const isf = presetData?.isf;
    const hblRelease = presetData?.hbl_release;

    const filtered_todo = todo?.filter((item) => item.filter_id === tab.id);
    let isHighlighted_prealert = filtered_todo?.length ? getClassName(filtered_todo, ['pre_alert'], 'header') : '';
    let isHighlighted_isf = filtered_todo?.length ? getClassName(filtered_todo, ['isf'], 'header') : '';
    let isHighlighted_hbl_release = filtered_todo?.length ? getClassName(filtered_todo, ['hbl_release'], 'header') : '';

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            hide: true,
        },
        {
            field: 'il_trade_partner_id_shipper',
            headerName: 'Shipper',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            // Copy/paste carries the whole partner object as JSON (same pattern as the bulk dialog
            // and location cells); a plain name or a location object is rejected before it can be saved.
            valueFormatter: objectValueFormatter,
            valueParser: createObjectValueParser({ key: 'il_trade_partner_id_shipper', label: 'Shipper' }, handleFormatMismatch),
            renderCell: (params) => {
                return (
                    <Typography sx={customized_style.itemDatagrid_Typography}>{params.value?.name || ''}</Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions.il_trade_partner_id_shipper.options}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'il_trade_partner_id_consignee',
            headerName: 'Consignee',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            // Copy/paste carries the whole partner object as JSON (same pattern as the bulk dialog
            // and location cells); a plain name or a location object is rejected before it can be saved.
            valueFormatter: objectValueFormatter,
            valueParser: createObjectValueParser({ key: 'il_trade_partner_id_consignee', label: 'Consignee' }, handleFormatMismatch),
            renderCell: (params) => {
                return (
                    <Typography sx={customized_style.itemDatagrid_Typography}>{params.value?.name || ''}</Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions.il_trade_partner_id_consignee.options}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'hbl_link',
            headerName: 'Link',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <Link href={params.value ? `${params.value}` : '#'} target="_blank" rel="noopener">
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'reference',
            headerName: 'Reference',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>
            ),
        },
        {
            field: 'hbl_quantity',
            headerName: 'QTY',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'number',
        },
        {
            field: 'il_opus_volume_unit_id',
            headerName: 'Unit',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderEditCell: (params) => (
                <CustomAutocompleteCell_string
                    params={params}
                    options={unitOptions}
                    handleInputChange={(event, value) => handleUnitChange(event, value)}
                    shouldFocus={true}
                />
            ),
        },
        {
            field: 'pre_alert',
            headerName: 'Status',
            flex: 1,
            editable: preAlert,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: getHeaderClass(preAlert, isHighlighted_prealert),
            renderCell: (params) => {
                return <span style={{ color: preAlert ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={prealert_status} />,
        },
        {
            field: 'isf',
            headerName: 'Status',
            flex: 1,
            editable: isf,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: getHeaderClass(isf, isHighlighted_isf),
            renderCell: (params) => {
                return <span style={{ color: isf ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={isf_status} />,
        },
        {
            field: 'isf_link',
            headerName: 'Link',
            flex: 0.6,
            editable: isf,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(isf, isHighlighted_isf),
            renderCell: (params) => {
                const linkStyle = !isf ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!isf) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'hbl_release',
            headerName: 'Status',
            flex: 1,
            editable: hblRelease,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: getHeaderClass(hblRelease, isHighlighted_hbl_release),
            renderCell: (params) => {
                return <span style={{ color: hblRelease ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={hbl_release} />,
        },
    ];
};

export const hblCols2 = (
    userRole,
    presetData,
    tab,
    todo,
    unitOptions,
    handleUnitChange,
    tradePartnerOptions,
    handleTradePartnerChange,
) => {
    const customs = presetData?.customs;
    const arrivalNotice = presetData?.arrival_notice;
    const invoice = presetData?.invoice;
    const crdr = presetData?.crdr;

    const filtered_todo = todo?.filter((item) => item.filter_id === tab.id);

    let isHighlighted_customs = filtered_todo?.length
        ? getClassName(filtered_todo, ['customs', 'customs_hold'], 'header')
        : '';

    let isHighlighted_arrivalnotice = filtered_todo?.length
        ? getClassName(filtered_todo, ['arrival_notice'], 'header')
        : '';
    let isHighlighted_invoice = filtered_todo?.length ? getClassName(filtered_todo, ['invoice'], 'header') : '';
    let isHighlighted_crdr = filtered_todo?.length ? getClassName(filtered_todo, ['crdr'], 'header') : '';

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            hide: true,
        },
        {
            field: 'customs',
            headerName: 'Status',
            flex: 1,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: getHeaderClass(customs, isHighlighted_customs),
            renderCell: (params) => {
                return <span style={{ color: customs ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={customs_status} />,
        },
        {
            field: 'customs_hold',
            headerName: 'Hold/Release',
            flex: 1,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: getHeaderClass(customs, isHighlighted_customs),
            renderCell: (params) => {
                return <span style={{ color: customs ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={customs_hold_status} />,
        },
        {
            field: 'customs_link',
            headerName: 'Link',
            flex: 0.7,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(customs, isHighlighted_customs),
            renderCell: (params) => {
                const linkStyle = !customs ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!customs) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'customs_other',
            headerName: 'Others',
            flex: 1,
            editable: customs,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(customs, isHighlighted_customs),
            renderCell: (params) => {
                return <span style={{ color: !customs ? '#C7C7C7' : 'inherit' }}>{params.value}</span>;
            },
        },
        {
            field: 'arrival_notice',
            headerName: 'Status',
            type: 'singleSelect',
            flex: 1,
            editable: arrivalNotice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(arrivalNotice, isHighlighted_arrivalnotice),
            renderCell: (params) => {
                return <span style={{ color: arrivalNotice ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={arrival_notice_status} />,
        },
        {
            field: 'arrival_notice_link',
            headerName: 'Link',
            flex: 0.7,
            editable: arrivalNotice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(arrivalNotice, isHighlighted_arrivalnotice),
            renderCell: (params) => {
                const linkStyle = !arrivalNotice ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!arrivalNotice) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'invoice',
            headerName: 'Status',
            type: 'singleSelect',
            flex: 1,
            editable: invoice,
            align: 'center',
            headerAlign: 'center',
            valueOptions: invoice_crdr_status,
            headerClassName: getHeaderClass(invoice, isHighlighted_invoice),
            renderCell: (params) => {
                return <span style={{ color: invoice ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => {
                const allowedStatuses = ['Pending', 'Ready', 'Sent'];
                const hasOperationManagerRole =
                    isAssistantManagerOrAbove(userRole);
                return (
                    <CustomSelectCell_string
                        params={params}
                        options={invoice_crdr_status}
                        hasOperationManagerRole={hasOperationManagerRole}
                        allowedStatuses={allowedStatuses}
                    />
                );
            },
        },
        {
            field: 'invoice_link',
            headerName: 'Link',
            flex: 0.7,
            editable: invoice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(invoice, isHighlighted_invoice),
            renderCell: (params) => <MultiLinkCell params={params} enabled={invoice} />,
            renderEditCell: (params) => <MultiLinkEditCell params={params} />,
        },
        {
            field: 'invoice_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 0.7,
            editable: invoice,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(invoice, isHighlighted_invoice),
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const formattedDate = params.value
                    ? params.value.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: invoice ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'crdr',
            headerName: 'Status',
            type: 'singleSelect',
            flex: 1,
            editable: crdr,
            align: 'center',
            headerAlign: 'center',
            valueOptions: invoice_crdr_status,
            headerClassName: getHeaderClass(crdr, isHighlighted_crdr),
            renderCell: (params) => {
                return <span style={{ color: crdr ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => {
                const allowedStatuses = ['Pending', 'Ready', 'Sent'];
                const hasOperationManagerRole =
                    isAssistantManagerOrAbove(userRole);
                return (
                    <CustomSelectCell_string
                        params={params}
                        options={invoice_crdr_status}
                        hasOperationManagerRole={hasOperationManagerRole}
                        allowedStatuses={allowedStatuses}
                    />
                );
            },
        },
        {
            field: 'crdr_link',
            headerName: 'Link',
            flex: 0.7,
            editable: crdr,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(crdr, isHighlighted_crdr),
            renderCell: (params) => <MultiLinkCell params={params} enabled={crdr} />,
            renderEditCell: (params) => <MultiLinkEditCell params={params} />,
        },
        {
            field: 'crdr_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 0.7,
            editable: crdr,
            align: 'center',
            headerAlign: 'center',
            headerClassName: getHeaderClass(crdr, isHighlighted_crdr),
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const formattedDate = params.value
                    ? params.value.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';

                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: crdr ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
    ];
};

export const cntrCols = (
    handleDeleteClick,
    presetData,
    unitOptions,
    handleUnitChange,
    tradePartnerOptions,
    handleTradePartnerChange,
    matchedTodoContainer,
    handleLinkClick,
    mainRow,
    handleFillDown,
    onInvalidOption,
) => {
    const pierpass = presetData?.pierpass;
    const deliveryOrder = presetData?.delivery_order;
    const proof = presetData?.proof;
    const empty = presetData?.empty;

    return [
        {
            ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
            minWidth: 40,
            renderCell: (params) => {
                return (
                    <CustomDetailPanelToggle
                        id={params.id}
                        tooltip="Split"
                        icon={CallSplitIcon}
                        sx={{ ...customized_style.itemDatagrid_Typography, width: '40px' }}
                        iconSx={{ bgcolor: '#003a52', borderRadius: 50, color: 'white' }}
                    />
                );
            },
        },
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            hide: true,
        },
        {
            field: 'cntr_number',
            headerName: 'Number',
            flex: 1,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => <FillDownHeader label="Number" field="cntr_number" onFillDown={handleFillDown} />,
        },
        {
            field: 'volume',
            headerName: 'Vol',
            flex: 0.6,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            valueOptions: containerSizeList,
            renderHeader: () => <FillDownHeader label="Vol" field="volume" onFillDown={handleFillDown} />,
            valueGetter: (params) => {
                return params ?? '';
            },
            pastedValueParser: createOptionPasteParser(containerSizeList, onInvalidOption),
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={containerSizeList} />,
        },
        {
            field: 'hazmat',
            headerName: 'Hazmat',
            flex: 0.8,
            editable: true,
            align: 'center',
            headerAlign: 'center',
            type: 'boolean',
            renderHeader: () => <FillDownHeader label="Hazmat" field="hazmat" onFillDown={handleFillDown} />,
            valueGetter: (value) => value === true,
        },
        {
            field: 'pierpass',
            headerName: 'Payment',
            type: 'singleSelect',
            flex: 0.9,
            editable: pierpass,
            align: 'center',
            headerAlign: 'center',
            headerClassName: pierpass ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Payment" field="pierpass" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(pierpass_import_status, onInvalidOption),
            cellClassName: (params) => {
                const filtered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                const className = getClassName(filtered_todo, 'pierpass', 'header-cell');
                return className;
            },
            renderCell: (params) => {
                return <span style={{ color: pierpass ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={pierpass_import_status} />,
        },
        {
            field: 'il_location_id_deliver_to',
            headerName: 'Delivery To',
            width: 150,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Delivery To" field="il_location_id_deliver_to" onFillDown={handleFillDown} />
            ),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => (
                <Typography
                    sx={{ ...customized_style.itemDatagrid_Typography, color: deliveryOrder ? 'inherit' : '#C7C7C7' }}
                >
                    {params.value}
                </Typography>
            ),
            renderEditCell: (params) => {
                return (
                    <GoogleRenderCell
                        params={params}
                        shouldFocus={true}
                        reducer={'setOimContainerFormData'}
                        tableName={'il_ocean_import_cntr'}
                        hasRowUpdate={false}
                        tableId={[mainRow.id, params.row.il_ocean_import_hbl_id]}
                    />
                );
            },
        },
        {
            field: 'il_trade_partner_id',
            headerName: 'Trucker Name',
            flex: 1.2,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => (
                <FillDownHeader label="Trucker Name" field="il_trade_partner_id" onFillDown={handleFillDown} />
            ),
            valueParser: createTradePartnerValueParser(
                'il_trade_partner_id',
                tradePartnerOptions?.il_trade_partner_id?.options,
            ),
            valueFormatter: tradePartnerValueFormatter,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: deliveryOrder ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {params.value?.name}
                    </Typography>
                );
            },
            renderEditCell: (params) => {
                return (
                    <CustomAutocompleteCell_TradePartner
                        params={params}
                        options={tradePartnerOptions.il_trade_partner_id.options}
                        handleInputChange={handleTradePartnerChange}
                    />
                );
            },
        },
        {
            field: 'delivery_order',
            headerName: 'Status',
            flex: 0.9,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="delivery_order" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(delivery_order_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: deliveryOrder ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={delivery_order_status} />,
        },
        {
            field: 'proof',
            headerName: 'Status',
            flex: 0.9,
            editable: proof,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: proof ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="proof" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(proof_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'proof', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: proof ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={proof_status} />,
        },
        {
            field: 'proof_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 0.7,
            editable: proof,
            align: 'center',
            headerAlign: 'center',
            headerClassName: proof ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Date" field="proof_date" onFillDown={handleFillDown} />,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'proof', 'header-cell');
            },
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const formattedDate = params.value
                    ? params.value.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';
                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: proof ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'perdiem',
            headerName: 'Detention',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 1,
            editable: empty,
            align: 'center',
            headerAlign: 'center',
            headerClassName: empty ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Detention" field="perdiem" onFillDown={handleFillDown} />,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'empty', 'header-cell');
            },
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const formattedDate = params.value
                    ? params.value.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';
                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: empty ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'empty',
            headerName: 'Status',
            flex: 1,
            editable: empty,
            align: 'center',
            headerAlign: 'center',
            type: 'singleSelect',
            headerClassName: empty ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Status" field="empty" onFillDown={handleFillDown} />,
            pastedValueParser: createOptionPasteParser(empty_status, onInvalidOption),
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'empty', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: empty ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            renderEditCell: (params) => <CustomSelectCell_string params={params} options={empty_status} />,
        },
        {
            field: 'empty_date',
            headerName: 'Date',
            type: 'date',
            pastedValueParser: parsePastedDate,
            flex: 0.7,
            editable: empty,
            align: 'center',
            headerAlign: 'center',
            headerClassName: empty ? '' : 'disabled-header',
            renderHeader: () => <FillDownHeader label="Date" field="empty_date" onFillDown={handleFillDown} />,
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'empty', 'header-cell');
            },
            valueGetter: (params) => {
                return parseDateToLocal(params);
            },
            renderCell: (params) => {
                const formattedDate = params.value
                    ? params.value.toLocaleDateString('en-US', {
                          year: '2-digit',
                          month: '2-digit',
                          day: '2-digit',
                      })
                    : '';
                return (
                    <Typography
                        sx={{
                            ...customized_style.itemDatagrid_Typography,
                            color: empty ? 'inherit' : '#C7C7C7',
                        }}
                    >
                        {formattedDate}
                    </Typography>
                );
            },
            renderEditCell: (params) => <DateTypingEditCell params={params} />,
        },
        {
            field: 'actions',
            headerName: '',
            flex: 0.5,
            editable: false,
            align: 'center',
            headerAlign: 'center',
            type: 'actions',
            getActions: (params) => {
                const needsDeliveryOrder = presetData?.delivery_order && !params.row.delivery_order_link;
                const needsProof = presetData?.proof && !params.row.proof_link;
                const needsPickupNumber = presetData?.intermodal && !params.row.pickup_number;
                const shouldInput = needsDeliveryOrder || needsProof || needsPickupNumber;

                return [
                    <GridActionsCellItem
                        icon={<FileUploadIcon sx={{ color: shouldInput ? '#f48704' : 'rgba(0, 0, 0, 0.54)' }} />}
                        label="Link"
                        onClick={() => handleLinkClick(params.row)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon sx={{ color: 'rgba(0, 0, 0, 0.54)' }} />}
                        label="Delete"
                        onClick={() => handleDeleteClick(params.id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];
};
export const linkCols = (presetData, matchedTodoContainer) => {
    const deliveryOrder = presetData?.delivery_order;
    const proof = presetData?.proof;
    const intermodal = presetData?.intermodal;

    return [
        {
            field: 'id',
            headerName: 'ID',
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            hideable: false,
            hide: true,
            pinnable: false,
        },
        {
            field: 'pickup_number',
            headerName: 'Pick Up Number',
            flex: 1,
            editable: intermodal,
            align: 'center',
            headerAlign: 'center',
            headerClassName: intermodal ? '' : 'disabled-header',
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'pickup_number', 'header-cell');
            },
            renderCell: (params) => {
                return <span style={{ color: intermodal ? 'inherit' : '#C7C7C7' }}>{params.value}</span>;
            },
            // DB column il_ocean_import_cntr.pickup_number is VARCHAR(25)
            renderEditCell: (params) => <GridEditInputCell {...params} inputProps={{ maxLength: 25 }} />,
        },
        {
            field: 'delivery_order_link',
            headerName: 'Delivery Order Link',
            flex: 1,
            editable: deliveryOrder,
            align: 'center',
            headerAlign: 'center',
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'delivery_order', 'header-cell');
            },
            renderCell: (params) => {
                const linkStyle = !deliveryOrder ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!deliveryOrder) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
        {
            field: 'proof_link',
            headerName: 'Proof Link',
            flex: 1,
            editable: proof,
            align: 'center',
            headerAlign: 'center',
            headerClassName: proof ? '' : 'disabled-header',
            cellClassName: (params) => {
                const fitlered_todo = matchedTodoContainer.filter((item) => item.filter_id === params.row.id);
                return getClassName(fitlered_todo, 'proof_link', 'header-cell');
            },
            renderCell: (params) => {
                const linkStyle = !proof ? { color: '#C7C7C7' } : {};
                const handleClick = (event) => {
                    if (!proof) {
                        event.preventDefault();
                    }
                };
                return (
                    <Link
                        href={params.value ? `${params.value}` : '#'}
                        target="_blank"
                        rel="noopener"
                        style={linkStyle}
                        onClick={handleClick}
                    >
                        {params.value ? 'View File' : ''}
                    </Link>
                );
            },
        },
    ];
};

export const aliasCol = (
    handleDeleteClick,
    handleAddClick,
    tradePartnerOptions,
    handleTradePartnerChange,
    unitOptions,
    handleUnitChange,
    mainRow,
    containerRow,
    handleFillDown,
    onInvalidOption,
) => [
    {
        field: 'alias_name',
        headerName: 'Alias',
        align: 'center',
        headerAlign: 'center',
        flex: 1.4,
        editable: true,
        renderHeader: () => <FillDownHeader label="Alias" field="alias_name" onFillDown={handleFillDown} />,
    },
    {
        field: 'alias_bol_number',
        headerName: 'BOL',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
        renderHeader: () => <FillDownHeader label="BOL" field="alias_bol_number" onFillDown={handleFillDown} />,
    },
    {
        field: 'alias_bol_link',
        headerName: 'BOL Link',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        editable: true,
        renderHeader: () => <FillDownHeader label="BOL Link" field="alias_bol_link" onFillDown={handleFillDown} />,
        renderCell: (params) => {
            return (
                <Link href={params.value ? `${params.value}` : '#'} target="_blank" rel="noopener">
                    {params.value ? 'View File' : ''}
                </Link>
            );
        },
    },
    {
        field: 'alias_quantity',
        headerName: 'QTY',
        align: 'center',
        headerAlign: 'center',
        type: 'number',
        editable: true,
        flex: 0.6,
        renderHeader: () => <FillDownHeader label="QTY" field="alias_quantity" onFillDown={handleFillDown} />,
    },
    {
        field: 'il_opus_volume_unit_id',
        headerName: 'Unit',
        align: 'center',
        headerAlign: 'center',
        editable: true,
        flex: 0.8,
        renderHeader: () => <FillDownHeader label="Unit" field="il_opus_volume_unit_id" onFillDown={handleFillDown} />,
        valueParser: (value, row) => {
            if (value === null || value === undefined || value === '') return '';
            const names = (unitOptions || []).map((option) => (typeof option === 'string' ? option : option?.name));
            return names.includes(value) ? value : (row?.il_opus_volume_unit_id ?? '');
        },
        renderEditCell: (params) => (
            <CustomAutocompleteCell_string
                params={params}
                options={unitOptions}
                handleInputChange={(event, value) => handleUnitChange(event, value)}
                shouldFocus={true}
            />
        ),
    },
    {
        field: 'il_location_id_pick_up',
        headerName: 'Pick Up',
        align: 'center',
        headerAlign: 'center',
        editable: true,
        flex: 1.5,
        renderHeader: () => (
            <FillDownHeader label="Pick Up" field="il_location_id_pick_up" onFillDown={handleFillDown} />
        ),
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
        renderEditCell: (params) => {
            return (
                <GoogleRenderCell
                    params={params}
                    shouldFocus={true}
                    reducer={'setOimAliasFormData'}
                    tableName={'il_ocean_import_cntr_alias'}
                    hasRowUpdate={false}
                    tableId={[mainRow.id, containerRow.il_ocean_import_hbl_id, containerRow.id]}
                />
            );
        },
    },
    {
        field: 'il_location_id_deliver_to',
        headerName: 'Delivery To',
        align: 'center',
        headerAlign: 'center',
        editable: true,
        flex: 1.5,
        renderHeader: () => (
            <FillDownHeader label="Delivery To" field="il_location_id_deliver_to" onFillDown={handleFillDown} />
        ),
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
        renderEditCell: (params) => (
            <GoogleRenderCell
                params={params}
                shouldFocus={true}
                reducer={'setOimAliasFormData'}
                tableName={'il_ocean_import_cntr_alias'}
                hasRowUpdate={false}
                tableId={[mainRow.id, containerRow.il_ocean_import_hbl_id, containerRow.id]}
            />
        ),
    },
    {
        field: 'alias_etd',
        headerName: 'ETD',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        pastedValueParser: parsePastedDate,
        flex: 0.7,
        editable: true,
        renderHeader: () => <FillDownHeader label="ETD" field="alias_etd" onFillDown={handleFillDown} />,
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        renderCell: (params) => {
            const formattedDate = params.value
                ? params.value.toLocaleDateString('en-US', {
                      year: '2-digit',
                      month: '2-digit',
                      day: '2-digit',
                  })
                : '';

            return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    {
        field: 'alias_eta',
        headerName: 'ETA',
        align: 'center',
        headerAlign: 'center',
        flex: 0.7,
        type: 'date',
        pastedValueParser: parsePastedDate,
        editable: true,
        renderHeader: () => <FillDownHeader label="ETA" field="alias_eta" onFillDown={handleFillDown} />,
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        renderCell: (params) => {
            const formattedDate = params.value
                ? params.value.toLocaleDateString('en-US', {
                      year: '2-digit',
                      month: '2-digit',
                      day: '2-digit',
                  })
                : '';

            return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
    },
    {
        field: 'il_trade_partner_id',
        headerName: 'Trucker',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        editable: true,
        renderHeader: () => <FillDownHeader label="Trucker" field="il_trade_partner_id" onFillDown={handleFillDown} />,
        valueParser: createTradePartnerValueParser('il_trade_partner_id', tradePartnerOptions),
        valueFormatter: tradePartnerValueFormatter,
        renderCell: (params) => {
            return <Typography sx={customized_style.itemDatagrid_Typography}>{params.value?.name}</Typography>;
        },
        renderEditCell: (params) => {
            return (
                <CustomAutocompleteCell_TradePartner
                    params={params}
                    options={tradePartnerOptions}
                    handleInputChange={handleTradePartnerChange}
                />
            );
        },
    },
    {
        field: 'alias_shipment_type',
        headerName: 'Type',
        align: 'center',
        headerAlign: 'center',
        flex: 0.7,
        editable: true,
        renderHeader: () => <FillDownHeader label="Type" field="alias_shipment_type" onFillDown={handleFillDown} />,
        valueParser: createOptionValueParser('alias_shipment_type', trucking_type, onInvalidOption),
        renderCell: (params) => {
            return <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>;
        },
        renderEditCell: (params) => (
            <CustomAutocompleteCell
                params={params}
                options={trucking_type}
                value={params.value || ''}
                shouldFocus={true}
                useCustomComparisons={false}
            />
        ),
    },
    {
        field: 'actions',
        headerName: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.5,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        type: 'actions',
        getActions: ({ id }) => {
            return [
                <Tooltip title="Delete">
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label="Delete"
                        onClick={() => handleDeleteClick(id)}
                        color="inherit"
                    />
                </Tooltip>,
            ];
        },
        renderHeader: ({ id }) => (
            <Tooltip title="Add">
                <IconButton variant="text" size="small" onClick={handleAddClick}>
                    <AddCircleIcon sx={{ color: '#003a52' }} />
                </IconButton>
            </Tooltip>
        ),
    },
];

export const hblGroupingModel = (presetData, tabValue, historyData, tab) => {
    const preAlert = presetData?.pre_alert;
    const isf = presetData?.isf;
    const hblRelease = presetData?.hbl_release;
    const customs = presetData?.customs;

    return [
        {
            groupId: 'HBL',
            headerAlign: 'center',
            children: [
                { field: 'il_trade_partner_id_shipper' },
                { field: 'il_trade_partner_id_consignee' },
                { field: 'hbl_link' },
                { field: 'reference' },
            ],
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, [
                    'il_trade_partner_id_shipper',
                    'il_trade_partner_id_consignee',
                    'hbl_link',
                    'reference',
                ]),
        },
        {
            groupId: 'LCL',
            headerAlign: 'center',
            children: [{ field: 'hbl_quantity' }, { field: 'il_opus_volume_unit_id' }],
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, ['hbl_quantity', 'il_opus_volume_unit_id']),
        },
        {
            groupId: 'Pre Alert',
            headerAlign: 'center',
            children: [{ field: 'pre_alert' }],
            headerClassName: preAlert ? '' : 'disabled-header',
            renderHeaderGroup: (params) => createHeaderGroup(params, historyData, tabValue, tab, ['pre_alert']),
        },
        {
            groupId: 'ISF Filing',
            headerAlign: 'center',
            children: [{ field: 'isf' }, { field: 'isf_link' }],
            headerClassName: isf ? '' : 'disabled-header',
            renderHeaderGroup: (params) => createHeaderGroup(params, historyData, tabValue, tab, ['isf', 'isf_link']),
        },
        {
            groupId: 'HBL Release',
            headerAlign: 'center',
            children: [{ field: 'hbl_release' }],
            headerClassName: hblRelease ? '' : 'disabled-header',
            renderHeaderGroup: (params) => createHeaderGroup(params, historyData, tabValue, tab, ['hbl_release']),
        },
        {
            groupId: 'Customs',
            headerAlign: 'center',
            children: [
                { field: 'customs' },
                { field: 'customs_hold' },
                { field: 'customs_link' },
                { field: 'customs_other' },
            ],
            headerClassName: customs ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, [
                    'customs',
                    'customs_hold',
                    'customs_other',
                    'customs_link',
                ]),
        },
    ];
};

export const hblGroupingModel2 = (presetData, tabValue, historyData, tab) => {
    const customs = presetData?.customs;
    const arrival_notice = presetData?.arrival_notice;
    const invoice = presetData?.invoice;
    const crdr = presetData?.crdr;

    return [
        {
            groupId: 'Customs',
            headerAlign: 'center',
            children: [
                { field: 'customs' },
                { field: 'customs_hold' },
                { field: 'customs_link' },
                { field: 'customs_other' },
            ],
            headerClassName: customs ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, [
                    'customs',
                    'customs_hold',
                    'customs_other',
                    'customs_link',
                ]),
        },
        {
            groupId: 'Arrival Notice',
            headerAlign: 'center',
            children: [{ field: 'arrival_notice' }, { field: 'arrival_notice_link' }],
            headerClassName: arrival_notice ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, ['arrival_notice', 'arrival_notice_link']),
        },
        {
            groupId: 'Invoice',
            headerAlign: 'center',
            children: [{ field: 'invoice' }, { field: 'invoice_link' }, { field: 'invoice_date' }],
            headerClassName: invoice ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, ['invoice', 'invoice_link', 'invoice_date']),
        },
        {
            groupId: 'Credit/Debit Note',
            headerAlign: 'center',
            children: [{ field: 'crdr' }, { field: 'crdr_link' }, { field: 'crdr_date' }],
            headerClassName: crdr ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createHeaderGroup(params, historyData, tabValue, tab, ['crdr', 'crdr_link', 'crdr_date']),
        },
    ];
};

export const cntrGroupingModel = (presetData, tabValue, historyData, tab) => {
    const pierpass = presetData?.pierpass;
    const deliveryOrder = presetData?.delivery_order;
    const proof = presetData?.proof;
    const empty = presetData?.empty;

    return [
        {
            groupId: 'Container',
            headerAlign: 'center',
            children: [{ field: 'cntr_number' }, { field: 'volume' }, { field: 'hazmat' }],
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, ['cntr_number', 'volume', 'hazmat']),
        },
        {
            groupId: 'Pierpass',
            headerAlign: 'center',
            children: [{ field: 'pierpass' }],
            headerClassName: pierpass ? '' : 'disabled-header',
            renderHeaderGroup: (params) => createMultiHeaderGroup(params, historyData, tabValue, tab, ['pierpass']),
        },
        {
            groupId: 'Delivery Order',
            headerAlign: 'center',
            children: [
                { field: 'il_location_id_deliver_to' },
                { field: 'il_trade_partner_id' },
                { field: 'delivery_order' },
                { field: 'delivery_order_link' },
            ],
            headerClassName: deliveryOrder ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, [
                    'delivery_order',
                    'delivery_order_link',
                    'il_location_id_deliver_to',
                    'il_trade_partner_id',
                ]),
        },
        {
            groupId: 'Proof',
            headerAlign: 'center',
            children: [{ field: 'proof' }, { field: 'proof_link' }, { field: 'proof_date' }],
            headerClassName: proof ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, ['proof', 'proof_date', 'proof_link']),
        },
        {
            groupId: 'Empty Return',
            headerAlign: 'center',
            children: [{ field: 'perdiem' }, { field: 'empty_date' }, { field: 'empty' }],
            headerClassName: empty ? '' : 'disabled-header',
            renderHeaderGroup: (params) =>
                createMultiHeaderGroup(params, historyData, tabValue, tab, ['perdiem', 'empty_date', 'empty']),
        },
    ];
};
